// src/components/TeamSection.jsx
import { useTranslation } from "react-i18next";

function initials(name = "") {
  return name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export default function TeamSection({ className = "" }) {
  const { t } = useTranslation();
  const rawMembers = t("team.members", {
    returnObjects: true,
    defaultValue: [],
  });
  const members = Array.isArray(rawMembers) ? rawMembers : [];
  if (!members.length) {
    return (
      <section
        id="team"
        className={`mx-auto max-w-7xl px-4 py-16 ${className}`}
      >
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-100">
            {t("team.badge")}
            <span className="inline-block size-1 rounded-full bg-indigo-300" />
            {t("team.tagline")}
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">
            {t("team.heading")}
          </h2>
          <p className="mt-3 text-slate-600">{t("team.intro")}</p>
          <p className="mt-6 text-sm text-slate-500">
            {t(
              "team.empty_fallback",
              "Team will appear here once added to i18n."
            )}
          </p>
        </div>
      </section>
    );
  }
  return (
    <section id="team" className={`mx-auto max-w-7xl px-4 py-16 ${className}`}>
      {/* Header */}
      <div className="max-w-3xl">
        <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-100">
          {t("team.badge")}
          <span className="inline-block size-1 rounded-full bg-indigo-300" />
          {t("team.tagline")}
        </span>
        <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">
          {t("team.heading")}
        </h2>
        <p className="mt-3 text-slate-600">{t("team.intro")}</p>
      </div>

      {/* Grid */}
      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 items-stretch">
        {members.map((m, i) => (
          <article
            key={m.id || i}
            className="group h-full flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm ring-1 ring-transparent transition duration-300 hover:shadow-md"
          >
            {/* Content block grows */}
            <div className="flex-1 min-h-0">
              <div className="flex items-center gap-5">
                {m.photo ? (
                  <img
                    src={m.photo}
                    alt={t("team.alt_photo", { name: m.name })}
                    className="h-20 w-20 md:h-24 md:w-24 rounded-full object-cover ring-2 ring-indigo-100"
                    loading="lazy"
                  />
                ) : (
                  <div className="h-20 w-20 md:h-24 md:w-24 grid place-items-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 text-white text-xl font-semibold ring-2 ring-indigo-100">
                    {initials(m.name)}
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    {m.name}
                  </h3>
                  <p className="text-sm text-indigo-700 font-medium">
                    {m.role}
                  </p>
                </div>
              </div>

              {m.bio && (
                <p className="mt-4 text-sm leading-6 text-slate-700">
                  {m.bio}
                </p>
              )}
            </div>

            {/* Socials pinned to bottom with extra breathing room above */}
            {(m.links?.linkedin ||
              m.links?.github ||
              m.links?.x ||
              m.links?.website) && (
              <div className="mt-auto pt-4 md:pt-5 border-t border-slate-100 flex flex-wrap gap-3 text-sm">
                {m.links?.linkedin && (
                  <a
                    href={m.links.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${m.name} LinkedIn`}
                    className="inline-flex items-center gap-2 rounded-full px-3 py-1 ring-1 ring-slate-200 hover:bg-slate-50"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.5 8h4V24h-4V8zm7.5 0h3.8v2.2h.05c.53-1 1.82-2.2 3.75-2.2 4 0 4.75 2.63 4.75 6v10H15.3V16c0-1.9-.03-4.35-2.65-4.35-2.66 0-3.07 2.08-3.07 4.22V24H8V8z" />
                    </svg>
                    LinkedIn
                  </a>
                )}
                {m.links?.github && (
                  <a
                    href={m.links.github}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${m.name} GitHub`}
                    className="inline-flex items-center gap-2 rounded-full px-3 py-1 ring-1 ring-slate-200 hover:bg-slate-50"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 .5a12 12 0 00-3.79 23.4c.6.1.82-.26.82-.58v-2.02c-3.34.73-4.04-1.61-4.04-1.61-.55-1.4-1.34-1.77-1.34-1.77-1.09-.75.08-.74.08-.74 1.2.09 1.84 1.23 1.84 1.23 1.07 1.84 2.8 1.31 3.48 1 .11-.78.42-1.31.77-1.61-2.67-.3-5.47-1.34-5.47-5.98 0-1.32.47-2.4 1.24-3.25-.13-.3-.54-1.52.12-3.16 0 0 1.01-.32 3.3 1.24a11.4 11.4 0 016 0c2.28-1.56 3.29-1.24 3.29-1.24.66 1.64.25 2.86.12 3.16.77.85 1.23 1.93 1.23 3.25 0 4.66-2.8 5.68-5.48 5.98.43.37.82 1.1.82 2.22v3.29c0 .32.21.7.82.58A12 12 0 0012 .5z" />
                    </svg>
                    GitHub
                  </a>
                )}
                {m.links?.x && (
                  <a
                    href={m.links.x}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${m.name} X`}
                    className="inline-flex items-center gap-2 rounded-full px-3 py-1 ring-1 ring-slate-200 hover:bg-slate-50"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M18.244 2H21l-6.57 7.507L22 22h-6.828l-4.86-6.356L4.6 22H2l7.07-8.078L2 2h6.914l4.39 5.833L18.244 2zm-1.193 18h1.655L7.03 4H5.29l11.76 16z" />
                    </svg>
                    X
                  </a>
                )}
                {m.links?.website && (
                  <a
                    href={m.links.website}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${m.name} website`}
                    className="inline-flex items-center gap-2 rounded-full px-3 py-1 ring-1 ring-slate-200 hover:bg-slate-50"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm6.93 6H16.2a15 15 0 00-1.8-4.03A8.03 8.03 0 0118.93 8zM12 4.07c.74.9 1.68 2.53 2.16 3.93H9.84c.48-1.4 1.42-3.03 2.16-3.93zM4.07 12c0-.7.09-1.38.25-2h3.98a19 19 0 000 4H4.32c-.16-.62-.25-1.3-.25-2zm1 3h3.68c.38 1.45 1.05 2.93 1.87 4.03A8.03 8.03 0 015.07 15zm3.68-6H5.07A8.03 8.03 0 0110.62 5c-.82 1.1-1.49 2.58-1.87 4zM12 18.93c-.74-.9-1.68-2.53-2.16-3.93h4.32c-.48 1.4-1.42 3.03-2.16 3.93zM15.25 12a17 17 0 000-2h4.43c.22.64.32 1.32.32 2s-.1 1.36-.32 2h-4.43zm-5.5 0a17 17 0 000 2h4.5a17 17 0 000-2h-4.5zM13.13 19.03c.82-1.1 1.49-2.58 1.87-4.03h3.93a8.03 8.03 0 01-5.8 4.03z" />
                    </svg>
                    Website
                  </a>
                )}
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}
